import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import clsx from 'clsx';

import { LandingContent } from 'components/LandingContent';
import { Loading } from 'components/Loading';
import {
  archiveEmailThread,
  unarchiveEmailThread,
  getThreadCleanMessages,
  hitThreadCache,
  downloadEmailAttachment,
} from 'api/email';
import parse from 'html-react-parser';
import TooltipButton from 'components/Button/TooltipButton';
import { reduceCommaSeparatedString } from 'utils/strings';
import leadLandingImage from 'assets/images/leads-landing.png';
import { sanitizeHtml } from '../Notes/utils/html';

import './styles.scss';

const MessageHeader = ({ userDisplayName, userEmail, leadName, leadEmail, message, to, cc, bcc }) => {
  let displayName;

  for (let fromAddressIndex = 0; fromAddressIndex < message.from.length; fromAddressIndex+=1){
    const fromAddress = message.from[fromAddressIndex];
    if (fromAddress.email === userEmail) {
      displayName = userDisplayName;
      break;
    }
    if (fromAddress.email === leadEmail) {
      displayName = leadName;
      break;
    }
  }

  if (!displayName && message.from[0] && message.from[0].name) {
    displayName = message.from[0].name;
  }

  return (
    <>
      <div className="thread__head">
        <div className="thread__user-name">
          {displayName}
        </div>
        {to && (
          <div className="thread__recipient-name thread__recipient-name--to">
            To: {to}
          </div>
        )}
        {cc && (
          <div className="thread__recipient-name thread__recipient-name--cc">
            Cc: {cc}
          </div>
        )}
        {bcc && (
          <div className="thread__recipient-name thread__recipient-name--bcc">
            Bcc: {bcc}
          </div>
        )}
        <div className="thread__subject">
          {message.subject}
        </div>
      </div>
      <div className="thread__date">
        {dayjs(message.date * 1000).format('MMM DD, YYYY')}
      </div>
    </>
  );
};

const Messages = ({
  isLoading,
  handleDisplayToggle,
  threadMessages,
  orgId,
  thread,
  userEmail,
  leadEmail,
  setReplyMessage,
  showArchived,
  setShowArchived,
  onLoad,
  onDestroy,
  refreshThreads,
  readOnlyMode,
}) => {
  const threadId = thread.thread_id || thread.id;
  const [downloadError, setDownloadError] = useState({});

  function onReplyClick(message) {
    if (message.from[0].email === userEmail.email) {
      // if the message is from the user, flip the to/from
      // this is flipped back in the index of LeadsEmailer
      message.from = [...message.to, ...message.from]
        .filter((obj) => obj.email !== userEmail.email);
    }

    // if our email is in the cc, remove it
    message.cc = message.cc.filter((obj) => obj.email !== userEmail.email && obj.email !== message.from[0].email);
    message.bcc = message.bcc.filter((obj) => obj.email !== userEmail.email);

    // if the lead's email is not in the cc, add it
    // we check from here since it becomes the to in the index of LeadsEmailer
    if (
      !message.from.find((obj) => obj.email === leadEmail.email) &&
      !message.cc.find((obj) => obj.email === leadEmail.email)
    ) {
      message.cc.push(leadEmail);
    }

    setReplyMessage(message);
    handleDisplayToggle(false);
    window.scrollTo(0, 0);
  }
  async function handleArchiveClick(message) {
    await archiveEmailThread(orgId, thread.lead_id, message.thread_id);
    await refreshThreads();
    setShowArchived(true);
    onLoad();
  }
  async function handleUnarchiveClick(message) {
    await unarchiveEmailThread(orgId, thread.lead_id, message.thread_id);
    await refreshThreads();
    setShowArchived(false);
    onLoad();
  }

  async function handleEmailAttachment(attachment, grantId, messageId, leadId) {
    try {
      setDownloadError(prev => ({ ...prev, [messageId]: null }));
      const attachmentId = attachment.id;
      const response = await downloadEmailAttachment(orgId, leadId, grantId, messageId, attachmentId);
      // take base64 encoding and download file
      const { data } = response;
      const binary = window.atob(data.data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i+=1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: attachment.content_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(prev => ({ ...prev, [messageId]: 'Error downloading attachment, please try again' }));
    }
  }


  useEffect(() => {
    if (onLoad) {
      onLoad();
    }
    return () => {
      if (onDestroy) {
        onDestroy();
      }
    };
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      {
        threadMessages[threadId]?.sort((a,b) => b.date - a.date).map((message, index) => {
            const canReply = index === 0 && message.able_to_reply;
            const { to, cc, bcc } = message || {};
            const toString = to && reduceCommaSeparatedString(to.map(rec => rec.email));
            const ccString = cc && reduceCommaSeparatedString(cc.map(rec => rec.email));
            const bccString = bcc && reduceCommaSeparatedString(bcc.map(rec =>  rec.email));
            return (
              <div
                className="thread"
                key={message.id}
              >
                <MessageHeader
                  message={message}
                  userDisplayName={userEmail.name}
                  userEmail={userEmail.email}
                  leadName={leadEmail.name}
                  leadEmail={leadEmail.email}
                  to={toString}
                  cc={ccString}
                  bcc={bccString}
                />
                <div className="thread__content">
                  <div className="thread__text">
                    { parse(sanitizeHtml(message.body, false, [], ['target'])) }
                  </div>
                  { message.attachments && message.attachments.length ? (
                    <div className="thread__attachments">
                      {
                        downloadError &&
                        downloadError[message.id] &&
                        <div className="thread__error">{downloadError[message.id]}</div>
                      }
                      { message.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="thread__attachment"
                          onClick={
                            () => handleEmailAttachment(attachment, message.grant_id, message.id, thread.lead_id)
                          }
                        >
                          {attachment.filename}
                        </div>
                      ))}
                    </div>
                  ): null }

                  { index === 0 && !showArchived && !readOnlyMode ? (
                    <>
                      <TooltipButton
                        type="button"
                        className="button"
                        onClick={() => canReply && onReplyClick(message)}
                        disabled={!canReply}
                        mode={!canReply ? 'tooltip' : 'button'}
                        tooltipProps={{
                            message: 'This thread belongs to another member in your organization and is read-only.',
                            tooltipDelay: 50,
                            floatingProps: {
                              placement: 'top',
                            },
                        }}
                      >
                        Reply
                      </TooltipButton>
                      <button
                        type="button"
                        className="button button--unstyled gold--large"
                        onClick={() => handleArchiveClick(message)}
                      >
                        Archive
                      </button>
                    </>
                  ): null}
                  { index === 0 && showArchived && !readOnlyMode ? (
                    <button
                      type="button"
                      className="button button--unstyled gold--large"
                      onClick={() => handleUnarchiveClick(message)}
                    >
                      Unarchive
                    </button>
                  ): null}
                </div>
              </div>
            );
          },
        )
      }
    </>
  );
};

const Threads = ({
  title,
  userEmail,
  activeThreads,
  archivedThreads,
  refreshThreads,
  setReplyMessage,
  handleDisplayToggle,
  orgId,
  readOnlyMode,
  leadId,
  leadEmail,
  userGrantId,
}) => {
  const [showArchived, setShowArchived] = useState(archivedThreads.length && !activeThreads.length);
  // #TODO: Make this magic number configurable.
  const showAmount = 5;
  const [pageNumber, setPageNumber] = useState(0);
  const [threadMessages, setThreadMessages] = useState({});
  const combinedThreads = [...activeThreads, ...archivedThreads];
  const memoedThreads = useMemo(() => {
    return (showArchived ? archivedThreads : activeThreads)
      .sort((a, b) => b.latest_message_received_date - a.latest_message_received_date);
  }, [showArchived, archivedThreads, activeThreads, threadMessages]);
  const [selectedThread, setSelectedThread] = useState({});
  const [isLoading, setIsLoading] = useState({});

  const totalCount = memoedThreads.length || 0;
  const totalPages = Math.ceil(totalCount / showAmount);
  const startIndex = pageNumber * showAmount;
  const _endIndex = pageNumber * showAmount + showAmount;
  const endIndex = _endIndex >= totalCount ? totalCount : _endIndex;

  async function getThreadMessages(thread) {
    if (!Object.keys(thread).length) {
      return;
    }

    const threadId = thread.thread_id || thread.id;
    setIsLoading(prev => ({
      ...prev,
      [threadId]: true,
    }));

    try {
      const cacheMessages = await hitThreadCache(orgId, leadId, threadId);

      if (cacheMessages.ok && cacheMessages.data.length) {
        setThreadMessages(prev => ({
          ...prev,
          [threadId]: cacheMessages.data,
        }));
      }

      const sortedCacheMessages = cacheMessages.data.sort((a, b) => a.date - b.date);
      const opEmailAddress = sortedCacheMessages[0]?.from[0]?.email;

      const apiMessages = await getThreadCleanMessages(orgId, leadId, threadId, userGrantId, opEmailAddress);

      if (apiMessages.ok) {
        setThreadMessages(prev => ({
          ...prev,
          [threadId]: apiMessages.data,
        }));
      }
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.error('Error fetching thread messages', e);
    }

    setIsLoading(prev => ({
      ...prev,
      [threadId]: false,
    }));

    setSelectedThread(thread);
  }

  const showThread = (threadId) => {
    const activeThread = activeThreads.find(thread => thread.id === threadId);
    if (activeThread) {
      getThreadMessages(activeThread);
      return;
    }

    const archivedThread = archivedThreads.find(thread => thread.id === threadId);
    if (archivedThread) {
      getThreadMessages(archivedThread);
      setShowArchived(true);
    }
  };

  const memoedMessagesComponent = useMemo(() => (
    <Messages
      isLoading={isLoading[selectedThread.id || selectedThread.thread_id]}
      threadMessages={threadMessages}
      thread={selectedThread}
      orgId={orgId}
      userEmail={userEmail}
      leadEmail={leadEmail}
      setReplyMessage={setReplyMessage}
      showArchived={showArchived}
      setShowArchived={setShowArchived}
      handleDisplayToggle={handleDisplayToggle}
      refreshThreads={refreshThreads}
      onLoad={() => getThreadMessages(selectedThread)}
      readOnlyMode={readOnlyMode}
      userGrantId={userGrantId}
    />
  ), [isLoading, selectedThread, threadMessages, showArchived]);

  useEffect(() => {
    async function updateSelectedThread() {
      setSelectedThread(memoedThreads[0] || {});
      await getThreadMessages(memoedThreads[0] || {});
    }

    if (window.location.hash) {
      showThread(window.location.hash.slice(1));
      // Don't want to push state to history when removing the hash from the location, otherwise
      // will require back twice to get back to previous page.
      window.history.replaceState(null, '', `${window.location.origin}/${window.location.pathname}`);
      return;
    }

    updateSelectedThread();
  }, [memoedThreads]);

  return (
    <div className="email-threads">
      {title && (
        <h2 className="email-threads__title">
          { memoedThreads[0].thread_archived ? `Archived ${title}` : title }
        </h2>
      )}
      {combinedThreads.length ? (
        <>
          { archivedThreads.length > 0 && (!memoedThreads.length || !memoedThreads[0].thread_archived) ? (
            <button
              type="button"
              className="button button--unstyled gold"
              onClick={() => setShowArchived(true)}
            >
              View Archived Emails
            </button>
          ): null }
          { activeThreads.length > 0 && (!memoedThreads.length || memoedThreads[0].thread_archived) ? (
            <button
              type="button"
              className="button button--unstyled gold"
              onClick={() => setShowArchived(false)}
            >
              View Active Emails
            </button>
          ) : null }

          <div className="threads-container">
            <div className="thread-subjects">
              {memoedThreads
                .slice(startIndex, endIndex)
                .map((thread) => {
                  return (
                    <div
                      key={thread.id}
                      className={clsx('thread-subject', thread.id === selectedThread.id && 'active')}
                      onClick={() => getThreadMessages(thread)}
                    >
                      {thread.subject}
                    </div>
                  );
                })
              }
              <div className="threads-pagination pager">
                <div className="thread-pagination__counter">
                  Viewing {startIndex + 1}-{endIndex} of {totalCount} messages
                </div>
                <div className="threads-pagination__controls">
                  {totalCount > showAmount && (
                    <>
                      <button
                        type="button"
                        className="button--unstyled pager__button"
                        {... pageNumber === 0 ? {
                          disabled: true,
                        } : {
                          onClick: () => setPageNumber(pageNumber - 1),
                        }}
                      >
                        Prev
                      </button>
                      |
                      <button
                        type="button"
                        className="button--unstyled pager__button"
                        {... (pageNumber + 1)>= totalPages ? {
                          disabled: true,
                        } : {
                          onClick: () => setPageNumber(pageNumber + 1),
                        }}
                      >
                        Next
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="thread-messages">
              {memoedMessagesComponent}
            </div>
          </div>
        </>
      ) : (
        <EmptyThreads handleDisplayToggle={handleDisplayToggle} />
      )}
    </div>
  );
};

const EmptyThreads = ({ handleDisplayToggle }) => {
  const description =
    `Keep tabs on your client and lead related email conversations
    right here in your email center. Create a new email to get started.`;
  return (
    <div className="threads__empty-container">
      <LandingContent
        action={() => handleDisplayToggle(false)}
        title="Welcome to your Email Center."
        description={description}
        actionButtonTitle="Create new email"
        heroImage={leadLandingImage}
      />
    </div>
  );
};

Threads.propTypes = {
  activeThreads: PropTypes.array,
  archivedThreads: PropTypes.array,
};

export default Threads;
